/**
 * ActionBarStyles.tsx
 *
 * Visual primitives for the ActionBar system, as typed-CSS functional
 * components (the styled-components replacement).
 *
 * 2026-07-24 visual overhaul: brings the hotbar into the "ink & brass" family
 * used by PlayerInfoPanel and BuffBar — ink-panel container, brass edge-line
 * fitting, theme-tuned slots, and consistent typography/shadows. Drag/cooldown
 * states are still functional but now share the same material language.
 */

import React from 'react';
import {
  FONT_WEIGHT,
  FONTS,
  ACCENT,
  INK,
  SPACE,
  RADII,
  FONT_SIZE,
  COLORS,
  CHROME,
  inkPanel,
} from '../../theme';
import '../../primitives/tome/tome-tokens.css';
import '../../HUD/tome/hud-tome-base.css';
import '../../HUD/tome/hud-tome-a.css';
import '../../HUD/tome/hud-tome-b.css';
import '../../HUD/tome/hud-tome-c.css';
import './actionbar.css';

// ─── Animation name handles ──────────────────────────────────────────────────
// The old keyframe exports are preserved by name so any importer referencing
// them keeps compiling. They map to the CSS @keyframes in actionbar.css.

export const pulseGlow = 'ab-pulseGlow';
export const dropReadyPulse = 'ab-dropReadyPulse';
export const dropOverGlow = 'ab-dropOverGlow';
export const activatedFlash = 'ab-activatedFlash';
export const shimmer = 'ab-shimmer';

// ─── Band geometry (W2a, 2026-09-28) ─────────────────────────────────────────
// The natural border-box size of the hotbar substrate. The compact landscape
// HUD (hudCompactLayout.ts) scales the WHOLE band about its bottom-centre and
// reserves the resulting band height so the chat peek + quick menu can never
// sit on the action bar. Exports keep that derivation in one place; the slot
// geometry itself (58×58, gameplay-critical) is unchanged and still asserted
// by ActionBarStyles.test.tsx.

/** Slot edge in px — gameplay-critical; asserted verbatim by ActionBarStyles.test.tsx. */
export const ACTION_BAR_SLOT_SIZE = 58;
/** DragHint row height (its style below). */
export const ACTION_BAR_DRAG_HINT_HEIGHT = 14;
/** Hotbar slot count (slot 1..0) — the natural row spans all ten. */
export const ACTION_BAR_SLOT_COUNT = 10;
/**
 * Natural band height (border-box): 2×SPACE.sm vertical padding + 2×1 px
 * inkPanel border + DragHint + SPACE.xs row gap + one slot row.
 */
export const ACTION_BAR_NATURAL_HEIGHT =
  SPACE.sm * 2 + 2 + ACTION_BAR_DRAG_HINT_HEIGHT + SPACE.xs + ACTION_BAR_SLOT_SIZE;
/**
 * Natural band width (border-box): 2×SPACE.md horizontal padding + 2×1 px
 * inkPanel border + ten slots + nine SPACE.sm gaps.
 */
export const ACTION_BAR_NATURAL_WIDTH =
  SPACE.md * 2 + 2 + ACTION_BAR_SLOT_COUNT * ACTION_BAR_SLOT_SIZE + (ACTION_BAR_SLOT_COUNT - 1) * SPACE.sm;


// ─── Container ───────────────────────────────────────────────────────────────

type DivProps = React.HTMLAttributes<HTMLDivElement>;

const topEdgeStyle: React.CSSProperties = {
  position: 'absolute',
  left: SPACE.lg,
  right: SPACE.lg,
  top: 0,
  height: 2,
  backgroundImage: `url("${CHROME.panelEdgeLine()}")`,
  backgroundSize: '100% 100%',
  backgroundRepeat: 'no-repeat',
  opacity: 0.5,
  pointerEvents: 'none',
};

const containerStyle: React.CSSProperties = {
  ...inkPanel(),
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: SPACE.xs,
  padding: `${SPACE.sm}px ${SPACE.md}px`,
  userSelect: 'none',
};

export const ActionBarContainer: React.FC<DivProps> = ({ style, children, ...rest }) => (
  <div style={{ ...containerStyle, ...style }} {...rest}>
    <span className="hudt-ground" aria-hidden="true" />
    <div className="ab-top-edge" aria-hidden style={topEdgeStyle} />
    {children}
  </div>
);

const slotsRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: SPACE.sm,
  alignItems: 'center',
};

export const SlotsRow: React.FC<DivProps> = ({ style, children, ...rest }) => (
  <div style={{ ...slotsRowStyle, ...style }} {...rest}>
    {children}
  </div>
);

// ─── Individual Slot ─────────────────────────────────────────────────────────

export interface SlotProps extends DivProps {
  $isEmpty: boolean;
  $isOnCooldown: boolean;
  $isDragOver: boolean;
  $isGlobalDrag: boolean; // any drag is happening, this slot is a potential target
  $wasActivated: boolean;
}

export const Slot: React.FC<SlotProps> = ({
  $isEmpty,
  $isOnCooldown,
  $isDragOver,
  $isGlobalDrag,
  $wasActivated,
  className = '',
  style,
  children,
  ...rest
}) => {
  // Base inner-frame background (drag-over → ready → idle). Idle is the ink well
  // (INK.well→wellLo, style guide §2); the drag-state cyan/green are functional
  // affordances (not chrome) and stay as-is.
  const background = $isDragOver
    ? 'linear-gradient(145deg, rgba(80, 200, 255, 0.15), rgba(20, 80, 120, 0.4))'
    : $isGlobalDrag && $isEmpty
    ? 'linear-gradient(145deg, rgba(100, 220, 100, 0.08), rgba(20, 60, 20, 0.35))'
    : `linear-gradient(180deg, ${INK.well}, ${INK.wellLo})`;

  const borderColor = $isDragOver
    ? 'rgba(80, 200, 255, 0.9)'
    : $isGlobalDrag && $isEmpty
    ? 'rgba(100, 220, 100, 0.65)'
    : $isOnCooldown
    ? `${COLORS.danger}99`
    : ACCENT.brass; // idle brass fitting (§2)

  const boxShadow = $isDragOver
    ? '0 0 0 3px rgba(80, 200, 255, 0.7), 0 0 30px rgba(80, 200, 255, 0.5), inset 0 0 15px rgba(80, 200, 255, 0.12)'
    : $isGlobalDrag && $isEmpty
    ? '0 0 0 2px rgba(100, 220, 100, 0.5), 0 0 18px rgba(100, 220, 100, 0.3)'
    : `inset 0 1px 1px rgba(255, 235, 180, 0.15), inset 0 0 8px rgba(0, 0, 0, 0.6), 0 2px 6px rgba(0, 0, 0, 0.4)`;

  // The continuous drag animations + the activated-flash ::after live in CSS,
  // gated by modifier classes. :hover / :active live in CSS too.
  const classes = ['ab-slot', className];
  if ($wasActivated) classes.push('ab-slot--activated');
  if ($isDragOver) classes.push('ab-slot--drag-over');
  if ($isGlobalDrag && $isEmpty && !$isDragOver) classes.push('ab-slot--drop-ready');

  const slotStyle: React.CSSProperties = {
    width: ACTION_BAR_SLOT_SIZE,
    height: ACTION_BAR_SLOT_SIZE,
    borderRadius: RADII.lg,
    position: 'relative',
    overflow: 'visible',
    cursor: $isEmpty ? 'default' : 'pointer',
    transition: 'transform 0.12s ease, border-color 0.18s ease, box-shadow 0.18s ease',
    background,
    border: `1.5px solid ${borderColor}`,
    boxShadow,
    ...style,
  };

  return (
    <div className={classes.join(' ').trim()} style={slotStyle} {...rest}>
      <span className="hudt-socket" aria-hidden="true" />
      {children}
    </div>
  );
};

// Drop-zone cross-hair for empty slots when not dragging
const emptySlotInnerStyle: React.CSSProperties = {
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
  fontSize: FONT_SIZE.md,
  color: `${ACCENT.brassBright}35`,
  pointerEvents: 'none',
  letterSpacing: '1px',
};

export const EmptySlotInner: React.FC<DivProps> = ({ style, children, ...rest }) => (
  <div className="hudt-empty" style={{ ...emptySlotInnerStyle, ...style }} {...rest}>
    {children}
  </div>
);

// ─── Slot Decorations ─────────────────────────────────────────────────────────

const keyLabelStyle: React.CSSProperties = {
  position: 'absolute',
  top: '3px',
  left: '4px',
  fontSize: FONT_SIZE.xs,
  fontWeight: FONT_WEIGHT.bold,
  color: COLORS.textPrimary,
  pointerEvents: 'none',
  lineHeight: 1,
  fontFamily: FONTS.mono, // keybind chip reads mono (§2)
  zIndex: 10,
  letterSpacing: '0.04em',
  textShadow: '0 1px 3px rgba(0,0,0,0.95)',
  background: 'rgba(8, 10, 16, 0.72)',
  padding: '1px 4px',
  borderRadius: '3px',
  border: '1px solid rgba(196, 164, 105, 0.28)',
};

export const KeyLabel: React.FC<DivProps> = ({ style, children, ...rest }) => (
  <div className="hudt-key" style={{ ...keyLabelStyle, ...style }} {...rest}>
    {children}
  </div>
);

const manaBadgeStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: SPACE.xs,
  right: SPACE.xs,
  background: `linear-gradient(135deg, ${COLORS.mana}cc, ${COLORS.mana}99)`,
  border: `1px solid ${COLORS.mana}88`,
  borderRadius: RADII.sm,
  padding: '1px 3px',
  fontSize: FONT_SIZE.xs,
  color: COLORS.textPrimary,
  fontWeight: FONT_WEIGHT.bold,
  pointerEvents: 'none',
  zIndex: 10,
  lineHeight: 1,
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 1px 2px rgba(0, 0, 0, 0.5)',
};

export const ManaBadge: React.FC<DivProps> = ({ style, children, ...rest }) => (
  <div className="hudt-cost" style={{ ...manaBadgeStyle, ...style }} {...rest}>
    {children}
  </div>
);

const abilityIconStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 30,
  height: '100%',
  width: '100%',
  flexDirection: 'column',
  pointerEvents: 'none',
  position: 'relative',
  zIndex: 5,
};

export const AbilityIcon: React.FC<DivProps> = ({ style, children, ...rest }) => (
  <div style={{ ...abilityIconStyle, ...style }} {...rest}>
    {children}
  </div>
);

// Drop-target crosshair overlay shown during drag
export interface DropTargetCrosshairProps extends DivProps {
  $active: boolean;
}

export const DropTargetCrosshair: React.FC<DropTargetCrosshairProps> = ({
  $active,
  style,
  children,
  ...rest
}) => {
  const crosshairStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    borderRadius: RADII.lg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: FONT_SIZE.xl,
    opacity: $active ? 1 : 0,
    transition: 'opacity 0.15s',
    pointerEvents: 'none',
    zIndex: 20,
    color: `${COLORS.info}ee`,
    textShadow: `0 0 12px ${COLORS.info}`,
    ...style,
  };
  return (
    <div style={crosshairStyle} {...rest}>
      {children}
    </div>
  );
};

// ─── Drag Global State Indicator ─────────────────────────────────────────────

export interface DragHintProps extends DivProps {
  $visible: boolean;
}

export const DragHint: React.FC<DragHintProps> = ({ $visible, style, children, ...rest }) => {
  const hintStyle: React.CSSProperties = {
    fontSize: FONT_SIZE.sm,
    color: `${COLORS.success}ee`,
    textAlign: 'center',
    opacity: $visible ? 1 : 0,
    height: ACTION_BAR_DRAG_HINT_HEIGHT,
    lineHeight: `${ACTION_BAR_DRAG_HINT_HEIGHT}px`,
    whiteSpace: 'nowrap',
    transition: 'opacity 0.2s ease',
    fontWeight: FONT_WEIGHT.semibold,
    letterSpacing: '0.5px',
    textShadow: `0 0 8px ${COLORS.success}`,
    pointerEvents: 'none',
    ...style,
  };
  return (
    <div style={hintStyle} {...rest}>
      {children}
    </div>
  );
};

/**
 * Tones of the status row above the slots (it is the DragHint row, so the
 * band height never changes): a drag hint, a locked session bar, a notice,
 * or the empty-bar hint on a newly added extra bar.
 */
export type BarStatusTone = 'drag' | 'lock' | 'notice' | 'empty';

const STATUS_TONE: Record<BarStatusTone, React.CSSProperties> = {
  drag: {},
  lock: { color: COLORS.textAccent, textShadow: '0 1px 2px rgba(0, 0, 0, 0.9)', letterSpacing: '0.2px' },
  notice: { color: COLORS.warning, textShadow: '0 1px 2px rgba(0, 0, 0, 0.9)', letterSpacing: '0.2px' },
  empty: { color: `var(--tome-hud-ink-soft, ${COLORS.textSecondary})`, textShadow: '0 1px 2px rgba(0, 0, 0, 0.9)', letterSpacing: '0.2px' },
};

export const BarStatusLine: React.FC<DivProps & { $tone: BarStatusTone | null }> = ({ $tone, children, ...rest }) => (
  <DragHint $visible={$tone !== null} style={$tone ? STATUS_TONE[$tone] : undefined} {...rest}>
    {children}
  </DragHint>
);
